'use client';

import { AccessTimeRounded, ArrowForward } from '@mui/icons-material';
import { Box, Button, Container, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Image from 'next/image';
import { useState } from 'react';

import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ComponentDataHomepageContent } from '@/types';

import { newHomepageTypography } from './newHomepageStyles';

const DEFAULT_STEPS = [
  {
    title: 'Quick Profile',
    body: 'A few simple questions to understand your business funding needs.',
    iconSrc: '/assets/new-homepage/homepage-profile.svg',
  },
  {
    title: 'One-Click Connect',
    body: 'Verify your business financials securely through Illion, offering bank-grade security.',
    iconSrc: '/assets/new-homepage/homepage-click-connect.svg',
  },
  {
    title: 'Instant Smart Matching',
    body: 'Our matching engine analyses thousands of data points to find your best fit.',
    iconSrc: '/assets/new-homepage/homepage-ai.svg',
  },
  {
    title: 'Choose Your Loan',
    body: 'View your personalised dashboard of matched lenders and top rates.',
    iconSrc: '/assets/new-homepage/homepage-features-list.svg',
  },
];

const DEFAULT_PROFILE_ROWS = [
  {
    label: 'Minimum amount ($) required',
    value: '56,000.00',
    iconSrc: '/assets/new-homepage/search-loan-amount.svg',
  },
  {
    label: 'Purpose of loan',
    value: 'Day-to-Day',
    iconSrc: '/assets/new-homepage/search-loan-purpose.svg',
  },
  {
    label: 'Select required length in Months',
    value: '24 months',
    iconSrc: '/assets/new-homepage/search-loan-months.svg',
  },
];

function DetailRow({ iconSrc, label, value }: { iconSrc: string; label: string; value: string }) {
  const theme = useTheme();

  return (
    <Box maxWidth="100%">
      <Typography
        sx={{
          mb: '10px',
          color: theme.palette.brand.bodyText,
          fontSize: 14,
          fontWeight: 400,
          lineHeight: '21px',
          letterSpacing: '-0.01em',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          height: 46,
          borderRadius: 1,
          background: '#F8F9FA',
          px: 1,
          py: 0.75,
          color: theme.palette.brand.bodyText,
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1 }}>
          <Box
            component="img"
            src={iconSrc}
            alt=""
            aria-hidden="true"
            sx={{ width: 20, height: 20 }}
          />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.01em',
              color: 'brand.bodyText',
            }}
          >
            {value}
          </Typography>
        </Box>
        <Typography sx={{ color: theme.palette.purple[600], fontSize: 14, lineHeight: 1 }}>
          x
        </Typography>
      </Box>
    </Box>
  );
}

function FloatingProfileCard({
  title,
  buttonText,
  rows,
}: {
  title: string;
  buttonText: string;
  rows: { label: string; value: string; iconSrc: string }[];
}) {
  const theme = useTheme();
  return (
    <Box sx={{ width: { xs: 'min(100%, 280px)', sm: 300 } }}>
      <Box
        sx={{
          width: '100%',
          minHeight: { sm: 465 },
          borderRadius: 1.5,
          background: theme.palette.common.white,
          px: { xs: 3, sm: '30px' },
          pt: '30px',
          pb: '40px',
          border: `1px solid ${alpha(theme.palette.brand.primary, 0.05)}`,
          boxShadow: '1px 1px 20px rgba(0, 0, 0, 0.07)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
          <Typography
            component="h3"
            sx={{
              color: theme.palette.brand.primary,
              ...newHomepageTypography.h4Tight,
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          {rows.map((row) => (
            <DetailRow key={row.label} label={row.label} value={row.value} iconSrc={row.iconSrc} />
          ))}
        </Box>
        <Button
          variant="contained"
          disableRipple
          endIcon={<ArrowForward />}
          sx={{
            alignSelf: 'flex-start',
            height: 42,
            minWidth: 92,
            borderRadius: 1,
            background: theme.palette.brand.primary,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.2,
            px: 2.2,
            py: 1.2,
            gap: 0.8,
            pointerEvents: 'none',
            '&:hover,&:focus,&:active': {
              background: theme.palette.brand.primary,
              boxShadow: 'none',
            },
            '& .MuiButton-endIcon svg': { fontSize: 15 },
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
}

function ProfileVisual({
  cardTitle,
  cardButtonText,
  cardRows,
}: {
  cardTitle: string;
  cardButtonText: string;
  cardRows: { label: string; value: string; iconSrc: string }[];
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 'min(100%, 320px)', md: '100%' },
        minHeight: 480,
        maxWidth: { xs: '100%', md: 340, lg: 430 },
        boxSizing: 'border-box',
        mx: { xs: 'auto', lg: 0 },
        mt: { lg: 13 },
        pb: { xs: 12, lg: 14 },
        pr: { xs: 0, lg: 8 },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: 'min(100%, 260px)', sm: 318, md: 340, lg: 382 },
          height: { xs: 280, sm: 340, md: 340, lg: 382 },
          borderRadius: 2.5,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(137,104,253,0.18), rgba(203,67,173,0.08))',
          boxShadow: `0 20px 45px ${alpha(theme.palette.brand.primaryDark, 0.12)}`,
        }}
      >
        <Image
          src="/assets/new-homepage/using-laptop.webp"
          alt=""
          width={640}
          height={800}
          sizes="(max-width: 750px) 260px, 360px"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: '50% 42%',
            display: 'block',
          }}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          right: { xs: 0, lg: 34 },
          bottom: { xs: 0, lg: -34 },
          zIndex: 2,
        }}
      >
        <FloatingProfileCard title={cardTitle} buttonText={cardButtonText} rows={cardRows} />
      </Box>
    </Box>
  );
}

interface HowItWorksSectionProps {
  cms?: ComponentDataHomepageContent | null;
}

export function HowItWorksSection({ cms }: HowItWorksSectionProps = {}) {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);

  const sectionLabel = cms?.HiwSectionLabel || 'How it works';
  const headingAccent = cms?.HiwHeadingAccent || 'Fast';
  const headingMain = cms?.HiwHeadingMain || '& Frictionless';
  const subheading = cms?.HiwSubheading || 'Get matched to right lenders in just 5 minutes';

  const steps =
    cms?.HiwSteps && cms.HiwSteps.length > 0
      ? cms.HiwSteps.map((s, i) => ({
          title: s?.Title || DEFAULT_STEPS[i]?.title || '',
          body: s?.Body || DEFAULT_STEPS[i]?.body || '',
          iconSrc:
            getImageUrl({ image: s?.Icon as any, size: 'url' }) || DEFAULT_STEPS[i]?.iconSrc || '',
        }))
      : DEFAULT_STEPS;

  const profileCardTitle = cms?.HiwProfileCardTitle || 'Only essential details';
  const profileCardButtonText = cms?.HiwProfileCardButtonText || 'Next';
  const profileCardRows =
    cms?.HiwProfileCardRows && cms.HiwProfileCardRows.length > 0
      ? cms.HiwProfileCardRows.map((r, i) => ({
          label: r?.Label || DEFAULT_PROFILE_ROWS[i]?.label || '',
          value: r?.Value || DEFAULT_PROFILE_ROWS[i]?.value || '',
          iconSrc:
            getImageUrl({ image: r?.Icon as any, size: 'url' }) ||
            DEFAULT_PROFILE_ROWS[i]?.iconSrc ||
            '',
        }))
      : DEFAULT_PROFILE_ROWS;

  // Split subheading on "just X minutes" bold pattern
  const subheadingParts = subheading.match(/^(.*)(just .+)$/) ?? null;

  return (
    <Box
      component="section"
      sx={{ px: { xs: 0, md: 0 }, py: { xs: 7, md: 10 }, background: theme.palette.common.white }}
    >
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          background: '#F5F5F5',
          px: { xs: 2, md: 6 },
          pt: { xs: 5, md: 4.8 },
          pb: { xs: 5, md: 6.5 },
          borderRadius: { xs: 0, lg: 2 },
        }}
      >
        <Box
          component="svg"
          width={0}
          height={0}
          aria-hidden="true"
          focusable="false"
          sx={{ position: 'absolute' }}
        >
          <defs>
            <linearGradient id="how-it-works-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={theme.palette.brand.primarySecondary1} />
              <stop offset="100%" stopColor={theme.palette.brand.secondary1} />
            </linearGradient>
          </defs>
        </Box>

        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 2.8 } }}>
          <Typography sx={{ ...newHomepageTypography.sectionLabel, mb: 1.1 }}>
            {sectionLabel}
          </Typography>
          <Typography
            component="h2"
            sx={{ color: theme.palette.brand.bodyText, mb: 4, ...newHomepageTypography.h2 }}
          >
            <Box component="span" sx={{ color: theme.palette.brand.primary }}>
              {headingAccent}
            </Box>{' '}
            &amp; {headingMain.replace(/^&\s*/, '')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '45% 5% 50%',
              lg: 'minmax(430px, 1fr) 54px 406px',
            },
            gap: { xs: 4, md: 1, lg: 4.5 },
            alignItems: 'start',
            width: '100%',
            minWidth: 0,
            maxWidth: { md: 980 },
            mx: 'auto',
          }}
        >
          <ProfileVisual
            cardTitle={profileCardTitle}
            cardButtonText={profileCardButtonText}
            cardRows={profileCardRows}
          />

          {/* Timeline divider */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              height: 692,
              alignItems: 'flex-start',
              justifyContent: 'center',
              position: 'relative',
              mt: 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                width: 32,
                height: 32,
                display: 'grid',
                placeItems: 'center',
                color: theme.palette.brand.primary,
                background: '#F5F5F5',
              }}
            >
              <AccessTimeRounded sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ position: 'absolute', top: 32, width: 4, height: 640 }}>
              {[0, 1, 2, 3].map((segment) => (
                <Box
                  key={segment}
                  sx={{
                    position: 'absolute',
                    top: segment * 165,
                    left: 0,
                    width: 4,
                    height: 145,
                    borderRadius: 999,
                    background: '#ADB5BD',
                  }}
                />
              ))}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 4,
                  height: activeIndex * 165 + 145,
                  borderRadius: 999,
                  background: theme.palette.brand.primary,
                  transition: 'height 450ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            </Box>
          </Box>

          {/* Steps */}
          <Box sx={{ width: { xs: '100%', md: 406 }, minWidth: 0, maxWidth: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                mb: 1.25,
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  width: { md: 367 },
                  maxWidth: '100%',
                  fontSize: { xs: 16, md: 18 },
                  fontWeight: 400,
                  lineHeight: 1.2,
                  color: 'brand.bodyText',
                  textAlign: 'center',
                }}
              >
                {subheadingParts ? (
                  <>
                    {subheadingParts[1]}
                    <Box component="strong" sx={{ fontWeight: 900 }}>
                      {subheadingParts[2]}
                    </Box>
                  </>
                ) : (
                  subheading
                )}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'grid', gap: { xs: 1.8, md: 2.5 }, width: '100%', maxWidth: '100%' }}
            >
              {steps.map((step, i) => {
                const isActive = i === activeIndex;
                return (
                  <Box
                    key={step.title}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveIndex(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveIndex(i);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      gap: { xs: 1.5, md: 2.25 },
                      alignItems: 'center',
                      minHeight: { xs: 116, md: 'auto' },
                      height: { md: 145 },
                      boxSizing: 'border-box',
                      borderRadius: 2.5,
                      px: { xs: 2.25, md: 3.75 },
                      py: { xs: 2.25, md: 3.75 },
                      background: isActive
                        ? alpha(theme.palette.common.white, 0.4)
                        : 'rgba(248, 249, 250, 0.5)',
                      border: isActive ? '1px solid #6559BB' : '1px solid transparent',
                      boxShadow: isActive
                        ? `0 14px 30px ${alpha(theme.palette.brand.primary, 0.08)}`
                        : 'none',
                      transition:
                        'background 300ms ease, border-color 300ms ease, box-shadow 300ms ease',
                      outline: 'none',
                      '&:focus-visible': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.brand.primary, 0.25)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 42, md: 48 },
                        height: { xs: 42, md: 48 },
                        display: 'grid',
                        placeItems: 'center',
                        background: 'transparent',
                      }}
                    >
                      {step.iconSrc && (
                        <Box
                          component="img"
                          src={step.iconSrc}
                          alt=""
                          aria-hidden="true"
                          sx={{
                            width: { xs: 42, md: 48 },
                            height: { xs: 42, md: 48 },
                            display: 'block',
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: { xs: 0.8, md: 1.5 },
                        width: { md: 280 },
                        height: { md: 85 },
                        flex: { xs: 1, md: '1 1 auto' },
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="h3"
                        sx={{ color: theme.palette.brand.primary, ...newHomepageTypography.h4 }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'brand.bodyText',
                          fontSize: { xs: 14.5, md: 16 },
                          fontWeight: 400,
                          lineHeight: 1.4,
                        }}
                      >
                        {step.body}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
